/**
 * 🪓 **The photographed tools, as pictures the pack can draw.**
 *
 * 🎯 **haugur, 2026-09-02**, comparing the flat drawn icons with photographs of
 * the same tools: *"i would really like our tools in game to look like that
 * please."* 🔴 **He opened it himself and he was right:** the blocks are
 * photographs and the tools were flat two-tone shapes, which is two visual
 * languages in one game, and nobody had ever put them side by side.
 * `Design/samples/54`, `55` and `56` are that comparison.
 *
 * ## ⚠️ This does NOT replace the drawn icons, and must not
 *
 * 🔴 **`hud-parts.ts` still draws every tool as SVG paths, and `helditem.ts`
 * turns those paths into the triangles you hold in your hand** — *"SVGLoader
 * cannot make shapes out of a bitmap"*, in its own words. **Deleting the drawn
 * icon would empty the player's hand and fall back to a bare sleeve.**
 *
 * 👉 **So this is the PACK picture only**, and the two live side by side on
 * purpose. ⚠️ It also means the thing in your hand and the thing in your pack
 * are drawn from different art until the hand is done separately — **and that
 * one is the change that turned dev and staging black on 2026-09-01**, so it is
 * deliberately not attempted here.
 *
 * ## Why PNG, when `block-tiles.json` is raw RGBA
 *
 * A block tile is uploaded to the GPU, so it is raw bytes and this package stays
 * zero-dependency — *"a PNG would need a decoder written twice"*. **An item icon
 * has a different consumer**: it is drawn as an `<image href="…">` inside the
 * HUD's SVG, and **the DOM decodes PNG natively**. So PNG needs no decoder at
 * all and no canvas at runtime, which is one moving part fewer than a block icon
 * needs.
 */

import iconData from '../data/item-icons.json' with { type: 'json' };

/** How big the packed pictures are, in pixels. */
export const ITEM_ICON_SIZE: number = iconData.size;

const ICONS: ReadonlyMap<string, string> = new Map(
  Object.entries(iconData.icons).map(([key, b64]) => [key, `data:image/png;base64,${b64}`]),
);

/**
 * The picture for this item, as a data URI, or `undefined` if it has none.
 *
 * ⚠️ **`undefined` is the ordinary case and is not a fault.** Most items have no
 * photograph and are drawn in code; a caller that gets `undefined` falls back to
 * the drawn icon, which is what every item did before this file existed.
 */
export function itemIconUri(itemKey: string): string | undefined {
  return ICONS.get(itemKey);
}

/** Every item key that has a photograph. ⚠️ For tests and tools, not the HUD. */
export function itemsWithIcons(): readonly string[] {
  return [...ICONS.keys()];
}
